<template>
  <HrPageLayout
    title="Confirm my team"
    subtitle="Staff who selected you as their supervisor. Approve to add them under you, or reject so they can pick the correct supervisor."
    icon="👥"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Self Service' },
      { label: 'Confirm my team' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" :disabled="loading" @click="load">
        <i class="bx bx-refresh"></i> Refresh
      </button>
    </template>

    <div v-if="loading" class="hr-chart-card">
      <div class="hr-chart-body hr-loading-wrap hr-py-5">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted">Loading claims...</p>
      </div>
    </div>

    <template v-else>
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">Pending confirmation ({{ pendingTotal }})</h5>
        </div>
        <div class="hr-chart-body">
          <p v-if="!pending.length" class="hr-text-muted">No one is currently waiting for you to confirm them as reporting to you.</p>
          <div v-else class="table-wrap">
              <table class="table table-sm claims-table">
              <thead>
                <tr>
                  <th>Employee number</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Requested</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in pending" :key="row.id">
                  <td data-label="Employee number">{{ row.employee_emp_no }}</td>
                  <td data-label="Name">{{ row.employee_name }}</td>
                  <td data-label="Department">{{ row.employee_department || '—' }}</td>
                  <td data-label="Requested">{{ formatWhen(row.created_at) }}</td>
                  <td data-label="Actions" class="text-end">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" :disabled="busy" @click="decide(row, 'approve')">
                      Approve
                    </button>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" :disabled="busy" @click="openReject(row)">
                      Reject
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="pagination-bar" v-if="totalPages > 1">
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" :disabled="page <= 1 || loading" @click="changePage(page - 1)">
                Previous
              </button>
              <span>Page {{ page }} of {{ totalPages }}</span>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" :disabled="page >= totalPages || loading" @click="changePage(page + 1)">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">Recent decisions</h5>
        </div>
        <div class="hr-chart-body">
          <p v-if="!recent.length" class="hr-text-muted">No approved or rejected claims yet.</p>
          <ul v-else class="recent-list">
            <li v-for="row in recent" :key="'r-' + row.id">
              <strong>{{ row.employee_name }}</strong>
              ({{ row.employee_emp_no }}) —
              <span :class="'st-' + row.status">{{ row.status }}</span>
              <span class="when">{{ formatWhen(row.decided_at || row.updated_at) }}</span>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger hr-ml-2" style="padding: 2px 6px; font-size: 11px;" :disabled="busy" @click="undoDecision(row)">Undo</button>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <div v-if="rejectRow" class="modal-backdrop" @click.self="rejectRow = null">
      <div class="modal-card">
        <h3>Reject claim</h3>
        <p>{{ rejectRow.employee_name }} will be notified to select the correct supervisor.</p>
        <label>Reason (optional)
          <textarea v-model="rejectReason" class="hr-input" rows="3"></textarea>
        </label>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="rejectRow = null">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="busy" @click="decide(rejectRow, 'reject')">Send rejection</button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "SupervisorClaims",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      busy: false,
      pending: [],
      pendingTotal: 0,
      page: 1,
      totalPages: 1,
      pageSize: 20,
      recent: [],
      rejectRow: null,
      rejectReason: "",
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    formatWhen(v) {
      if (!v) return "—";
      const d = new Date(String(v).replace(" ", "T"));
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleString("en-KE");
    },
    async load(page = this.page) {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "getSupervisorClaims");
        fd.append("page", String(page));
        fd.append("limit", String(this.pageSize));
        const res = await execute(fd, authUrl);
        const d = res?.data?.data || {};
        this.pending = d.pending || [];
        this.recent = d.recent || [];
        this.pendingTotal = Number(d.pendingCount || 0);
        this.page = Number(d.page || page);
        this.totalPages = Math.max(1, Number(d.totalPages || 1));
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) this.load(page);
    },
    openReject(row) {
      this.rejectRow = row;
      this.rejectReason = "";
    },
    async decide(row, decision) {
      this.busy = true;
      const fd = new FormData();
      fd.append("function", "decideSupervisorClaim");
      fd.append("id", String(row.id));
      fd.append("decision", decision);
      if (decision === "reject") fd.append("reject_reason", this.rejectReason);
      try {
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Saved");
          this.rejectRow = null;
          await this.load();
        } else {
          toast.error(res?.data?.message || "Could not save");
        }
      } finally {
        this.busy = false;
      }
    },
    async undoDecision(row) {
      if (!window.confirm(`Are you sure you want to undo your decision for ${row.employee_name}? They will be notified to pick a new supervisor.`)) return;
      this.busy = true;
      const fd = new FormData();
      fd.append("function", "undoSupervisorClaim");
      fd.append("id", String(row.id));
      try {
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Decision undone");
          await this.load();
        } else {
          toast.error(res?.data?.message || "Could not undo decision");
        }
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
  .table-wrap { overflow: hidden; }
  .claims-table { min-width: 680px; }
  .pagination-bar { align-items: center; display: flex; gap: 12px; justify-content: center; margin-top: 14px; }
.recent-list { margin: 0; padding-left: 18px; }
.st-approved { color: #047857; text-transform: capitalize; }
.st-rejected { color: #b91c1c; text-transform: capitalize; }
.when { color: #64748b; font-size: 12px; margin-left: 6px; }
.modal-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,.45); z-index: 2000; display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; }
.modal-card { background: #fff; border-radius: 12px; padding: 18px; width: min(480px, 100%); display: flex; flex-direction: column; gap: 10px; }
.modal-card label { display: flex; flex-direction: column; gap: 6px; font-size: 13px; }
.hr-input { padding: 8px 10px; border: 1px solid #cbd5e1; border-radius: 8px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
@media (max-width: 640px) {
  .claims-table, .claims-table tbody, .claims-table tr, .claims-table td { display: block; }
  .claims-table { min-width: 0; }
  .claims-table thead { display: none; }
  .claims-table tr { border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 12px; padding: 8px 12px; }
  .claims-table td { border: 0; padding: 5px 0 5px 42%; position: relative; text-align: right; }
  .claims-table td::before { color: #64748b; content: attr(data-label); font-weight: 600; left: 0; position: absolute; text-align: left; width: 38%; }
  .claims-table td:last-child { display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; padding-left: 0; }
  .claims-table td:last-child::before { display: none; }
  .pagination-bar { flex-wrap: wrap; }
}
</style>
